/**
 * Pure helpers and constants for homepage local focus: insights derivation,
 * area cards, market volume, and location parsing.
 */

import { pipe } from 'utils/pipe';
import { CountryCodeList, type CountryCode } from 'types/countries';
import {
  provinceOrStateNameFromCode,
  provinceOrStateCodeFromName,
  stateCodeFromSlugMappings,
  provinceCodeFromSlugMappings,
} from 'utils/province_or_state';
import type { ProvinceOrStateCode } from 'utils/province_or_state';
import type { CityStats } from 'data/insights/types';
import type { TopCities } from 'data/insights/types';
import type { AreaCardData } from '../../local-focus/area-cards';
import type { PropertyTypeSegment, MarketVolumeData } from '../../local-focus/market-dynamics';
import { DEFAULT_VOLUME } from '../../local-focus/market-dynamics';
import type { InsightsListingType } from 'data/insights/types';

// ── Location ─────────────────────────────────────────────────

/**
 * Parse a location name into city + province.
 *   "Toronto, ON"                              → { city: "Toronto", province: "on" }
 *   "Kensington Market, Toronto, ON"           → { city: "Toronto", province: "on" }
 *   "Downtown, Old Toronto, Toronto, ON"       → { city: "Toronto", province: "on" }
 * City and province are always the last two comma-separated segments — nested
 * neighbourhood/borough names just add more segments in front.
 */
export function parseCityAndProvince(marketName: string, slug: string): { city: string; province: string } {
  const parts = marketName.split(', ');
  if (parts.length === 1) {
    return { city: marketName, province: slug.slice(-2).toLowerCase() };
  }
  const city = parts[parts.length - 2];
  const province = parts[parts.length - 1];
  return { city: city.trim(), province: province.trim().toLowerCase() };
}

/** Capitalize the first letter of every word (title-case). */
export function titleCase(str: string): string {
  return str.replace(/(^|[\s-])\p{L}/gu, c => c.toUpperCase());
}

/**
 * A city-level slug is `<city>-<2-letter province/state code>` (e.g. "tampa-fl").
 * Province/state landing slugs ("florida") and boundary/Google-Places slugs do
 * not match. This is the client-side mirror of the insights endpoint allow-list.
 */
export function isCitySlug(slug: string): boolean {
  if (!slug || slug.length <= 3) return false;
  const suffixCode = slug.slice(-2).toLowerCase();
  return (
    slug.charAt(slug.length - 3) === '-' &&
    Boolean(provinceOrStateNameFromCode(suffixCode as ProvinceOrStateCode))
  );
}

/** Country + province/state (+ optional city) needed to query location insights. */
export interface InsightsContext {
  country: CountryCode;
  provinceCode: ProvinceOrStateCode;
  city?: string;
}

/**
 * Resolve the insights query context from the resolved slug + display name.
 *   - City slug ("tampa-fl") → city stats scoped to the slug's province code.
 *   - Province/state slug ("florida") or province fallback → province-level only.
 * Returns null when no province/state can be resolved (e.g. empty or unknown slug).
 */
export function deriveInsightsContext(
  slug: string,
  areaName: string,
  isCA: boolean
): InsightsContext | null {
  if (!slug) return null;
  const country = isCA ? CountryCodeList.CANADA : CountryCodeList.UNITED_STATES;

  if (isCitySlug(slug)) {
    // The slug suffix is the authoritative province code; the display name only
    // supplies the city label for the stats query.
    const suffixCode = slug.slice(-2).toLowerCase() as ProvinceOrStateCode;
    const { city } = parseCityAndProvince(areaName || slug, slug);
    return { country, provinceCode: suffixCode, city };
  }

  // Province/state landing (incl. the province-fallback path): province-level
  // stats only. Resolve the code from the slug first, then the display name.
  const provinceCode =
    stateCodeFromSlugMappings[slug] ||
    provinceCodeFromSlugMappings[slug] ||
    provinceOrStateCodeFromName(areaName) ||
    null;
  return provinceCode ? { country, provinceCode: provinceCode as ProvinceOrStateCode } : null;
}

// Every insights-backed location is treated as resolving to a city+province
// pair — same as a neighbourhood search falling back to its city — so the
// name shown always reads like "Toronto, ON", not just "Toronto".
export function formatCityProvinceName(city: string, province: string): string {
  const cityName = titleCase(city);
  return province ? `${cityName}, ${province.toUpperCase()}` : cityName;
}

export interface DisplayUrlsParams {
  currentSlug: string;
  hasAreaPage: boolean;
  /** City-level slug the insights sections resolved to — always a real area page. */
  insightsSlug: string;
  currentLatitude: number;
  currentLongitude: number;
  currentAreaName: string;
  homesForSaleUrl?: string;
  displayLocationName: string;
  insightsLocationName: string;
}

export interface DisplayUrls {
  /** "View all" URL for the exact search (Listings, ExpertGuidance). */
  currentHomesForSaleUrl: string;
  /** "X Listings in {name}" heading name — falls back to the city+province
   * together with currentHomesForSaleUrl, so a searched neighbourhood with no
   * dedicated area page doesn't name itself while linking to the city page. */
  listingsDisplayName: string;
  /** "View all" URL for the insights sections — always a real city area page. */
  insightsHomesForSaleUrl: string;
}

/**
 * Derives the "View all" URLs and paired display name for the local homepage.
 * For non-area-page locations (e.g. a Google Places neighbourhood with no
 * dedicated area page), falls back to the city-level real-estate page —
 * insightsSlug is resolved the same way the insights sections are, so this
 * stays a real, working page instead of a raw /search?lat/lng link.
 * /search?lat/lng is only used as a last resort, before insights resolve.
 */
export function deriveDisplayUrls({
  currentSlug,
  hasAreaPage,
  insightsSlug,
  currentLatitude,
  currentLongitude,
  currentAreaName,
  homesForSaleUrl,
  displayLocationName,
  insightsLocationName,
}: DisplayUrlsParams): DisplayUrls {
  const currentHomesForSaleUrl = (() => {
    if (currentSlug && hasAreaPage) return `/${currentSlug}-real-estate`;
    if (insightsSlug) return `/${insightsSlug}-real-estate`;
    if (currentSlug && currentLatitude && currentLongitude) {
      const areaName = encodeURIComponent(currentAreaName);
      return `/search?latitude=${currentLatitude}&longitude=${currentLongitude}&zoom=12&area-name=${areaName}`;
    }
    return homesForSaleUrl || '';
  })();

  const insightsHomesForSaleUrl = insightsSlug ? `/${insightsSlug}-real-estate` : (homesForSaleUrl || '');

  return {
    currentHomesForSaleUrl,
    listingsDisplayName: insightsLocationName || displayLocationName,
    insightsHomesForSaleUrl,
  };
}

// ── Market dynamics (property types, volume, aggregate) ──────

const TYPE_COLORS = {
  detached: '#6ee7b7',
  condo: '#67b8f0',
  townhouse: '#1b489b',
  semiDetached: '#f97066',
  link: '#fbbf24',
  condoTown: '#f472b6',
  other: '#c4b5fd',
};

type PriceBucket = { label: string; min: number; max: number };

const PRICE_BUCKETS: PriceBucket[] = [
  { label: 'Under $500k', min: 0, max: 500_000 },
  { label: '$500k - $1M', min: 500_000, max: 1_000_000 },
  { label: '$1M - $1.5M', min: 1_000_000, max: 1_500_000 },
  { label: '$1.5M - $2.5M', min: 1_500_000, max: 2_500_000 },
  { label: '$2.5M - $5M', min: 2_500_000, max: 5_000_000 },
  { label: '$5M+', min: 5_000_000, max: Infinity },
];

/** Monthly rent ranges — the for-sale ranges would put every rental under $500k. */
const RENT_PRICE_BUCKETS: PriceBucket[] = [
  { label: 'Under $1.5k', min: 0, max: 1_500 },
  { label: '$1.5k - $2.5k', min: 1_500, max: 2_500 },
  { label: '$2.5k - $3.5k', min: 2_500, max: 3_500 },
  { label: '$3.5k - $5k', min: 3_500, max: 5_000 },
  { label: '$5k+', min: 5_000, max: Infinity },
];

function bucketIndex(buckets: PriceBucket[], avgPrice: number): number {
  return buckets.findIndex(b => avgPrice >= b.min && avgPrice < b.max);
}

/** For-sale property shapes only — rentals are excluded (different dimension than building type). */
export function derivePropertyTypes(stats: CityStats): PropertyTypeSegment[] {
  const segments: PropertyTypeSegment[] = [];
  if (stats.TotalHouses > 0) segments.push({ label: 'House', count: stats.TotalHouses, color: TYPE_COLORS.detached });
  if (stats.TotalCondos > 0) segments.push({ label: 'Condo Apt', count: stats.TotalCondos, color: TYPE_COLORS.condo });
  if (stats.TotalTownhouses > 0) segments.push({ label: 'Townhouse', count: stats.TotalTownhouses, color: TYPE_COLORS.townhouse });
  segments.sort((a, b) => b.count - a.count);
  return segments;
}

export function deriveVolume(stats: CityStats, listingType: InsightsListingType = 'sale'): MarketVolumeData {
  const priceBuckets = listingType === 'rent' ? RENT_PRICE_BUCKETS : PRICE_BUCKETS;
  const houses = stats.TotalHouses || 0;
  const townhouses = stats.TotalTownhouses || 0;
  const condos = stats.TotalCondos || 0;
  const total = houses + townhouses + condos;
  if (total === 0) return DEFAULT_VOLUME;

  const bucketCounts = new Array(priceBuckets.length).fill(0);
  const typeData = [
    { count: condos, avgPrice: stats.AvgCondoPrice || 0 },
    { count: townhouses, avgPrice: stats.AvgTownhousePrice || 0 },
    { count: houses, avgPrice: stats.AvgHousePrice || 0 },
  ];
  for (const td of typeData) {
    if (td.count > 0 && td.avgPrice > 0) {
      const idx = bucketIndex(priceBuckets, td.avgPrice);
      if (idx >= 0) bucketCounts[idx] += td.count;
    }
  }
  const activeRanges = priceBuckets
    .map((bucket, i) => ({ label: bucket.label, count: bucketCounts[i] }))
    .filter(r => r.count > 0);
  if (activeRanges.length === 0) return DEFAULT_VOLUME;

  const maxRange = activeRanges.reduce((a, b) => (a.count > b.count ? a : b), activeRanges[0]);
  return {
    totalListings: total,
    mostActiveRange: maxRange.label,
    priceRanges: activeRanges.map(r => ({
      label: r.label,
      percentage: Math.round((r.count / total) * 100),
      isActive: r === maxRange,
    })),
  };
}

export function aggregateCityStats(statsArray: CityStats[]): CityStats | null {
  if (statsArray.length === 0) return null;
  if (statsArray.length === 1) return statsArray[0];
  const base = { ...statsArray[0] };

  for (let i = 1; i < statsArray.length; i++) {
    const s = statsArray[i];
    base.TotalHouses += s.TotalHouses || 0;
    base.TotalCondos += s.TotalCondos || 0;
    base.TotalTownhouses += s.TotalTownhouses || 0;
    base.TotalRentals += s.TotalRentals || 0;
  }
  const totals = statsArray.map(s => (s.TotalHouses || 0) + (s.TotalCondos || 0) + (s.TotalTownhouses || 0));
  const grandTotal = totals.reduce((a, b) => a + b, 0);

  if (grandTotal > 0) {
    const weightedAvg = (field: keyof CityStats) =>
      statsArray.reduce((acc, s, i) => acc + ((s[field] as number) || 0) * totals[i], 0) / grandTotal;
    base.AvgTotalPrice = Math.round(weightedAvg('AvgTotalPrice'));
    base.AvgHousePrice = Math.round(weightedAvg('AvgHousePrice'));
    base.AvgCondoPrice = Math.round(weightedAvg('AvgCondoPrice'));
    base.AvgTownhousePrice = Math.round(weightedAvg('AvgTownhousePrice'));
    base.DaysOnMarket = Math.round(weightedAvg('DaysOnMarket'));
    base.SellListRatio = Math.round(weightedAvg('SellListRatio') * 10) / 10;
    base.AvgPricePerSqft = Math.round(weightedAvg('AvgPricePerSqft'));
    base.MedianRentPrice = Math.round(weightedAvg('MedianRentPrice'));
    base.MedianPurchasePrice = Math.round(weightedAvg('MedianPurchasePrice'));
  }
  const allNeighbourhoods = statsArray.flatMap(s => s.topNeighbourhoods || []);
  allNeighbourhoods.sort((a, b) => b.count - a.count);
  base.topNeighbourhoods = allNeighbourhoods.slice(0, 10);
  const longestTrend = statsArray.reduce(
    (best, s) => ((s.medianPriceTrends?.length || 0) > (best?.length || 0) ? s.medianPriceTrends : best),
    statsArray[0].medianPriceTrends
  );
  base.medianPriceTrends = longestTrend;
  return base;
}

// ── Area cards ───────────────────────────────────────────────

const toSlugPart = (s: string) =>
  pipe(
    s,
    (x: string) => x.toLowerCase(),
    (x: string) => x.replace(/[^a-z0-9]+/g, '-'),
    (x: string) => x.replace(/(^-|-$)/g, '')
  );

export function deriveAreaCards(stats: CityStats, citySlug: string): AreaCardData[] {
  const neighbourhoods = stats.topNeighbourhoods || [];
  if (neighbourhoods.length === 0) return [];
  return neighbourhoods.slice(0, 5).map((n, index) => {
    const namePart = toSlugPart(n.name);
    const isFeatured = index === 0;
    const slug = citySlug ? `${namePart}-${citySlug}` : namePart;
    return {
      name: titleCase(n.name),
      slug,
      listingCount: n.count,
      isFeatured,
      description: isFeatured
        ? `${n.count.toLocaleString()} active listings — the most active neighbourhood in the area.`
        : `${n.count.toLocaleString()} active listings`,
      medianPrice: isFeatured && stats.MedianPurchasePrice ? stats.MedianPurchasePrice : undefined,
    };
  });
}

export function topCitiesToAreaCards(topCities: TopCities[], contextLabel?: string): AreaCardData[] {
  if (!topCities?.length) return [];
  return topCities.map((tc, index) => {
    const isFeatured = index === 0;
    return {
      name: titleCase(tc.Name),
      slug: tc.Slug,
      listingCount: tc.TotalCount,
      isFeatured,
      description: isFeatured && contextLabel
        ? `${tc.TotalCount.toLocaleString()} active listings — the most active area in ${contextLabel}.`
        : `${tc.TotalCount.toLocaleString()} active listings`,
    };
  });
}
